import React, { ReactNode } from 'react';
import styles from './OutlineButton.module.css';

export interface OutlineButtonProps {
  /** The text label for the button */
  label: string;
  /** Icon to display before the label */
  iconBefore?: ReactNode;
  /** Icon to display after the label */
  iconAfter?: ReactNode;
  /** Whether the button is disabled */
  isDisabled?: boolean;
  /** Whether the button is in active state */
  isActive?: boolean;
  /** Click handler */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Additional CSS class names */
  className?: string;
  /** Button type */
  type?: 'button' | 'submit' | 'reset';
  /** Custom styles to override default styling */
  style?: React.CSSProperties;
  /** ARIA expanded state for dropdown buttons */
  'aria-expanded'?: boolean;
  /** ARIA controls for dropdown buttons */
  'aria-controls'?: string;
  /** ARIA disabled state */
  'aria-disabled'?: boolean;
  /** ARIA role */
  role?: string;
  /** Ref to the button element */
  ref?: React.Ref<HTMLButtonElement>;
}

export function OutlineButton({
  label,
  iconBefore,
  iconAfter,
  isDisabled = false,
  isActive = false,
  onClick,
  className = '',
  type = 'button',
  style = {},
  ref,
  ...ariaProps
}: OutlineButtonProps) {
  const buttonClasses = [
    styles['outline-button'],
    isDisabled && styles['outline-button--disabled'],
    isActive && styles['outline-button--active'],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={isDisabled}
      style={style}
      {...ariaProps}
    >
      {iconBefore && (
        <span className={styles['outline-button__icon--before']}>
          {iconBefore}
        </span>
      )}
      <span className={styles['outline-button__label']}>{label}</span>
      {iconAfter && (
        <span className={styles['outline-button__icon--after']}>
          {iconAfter}
        </span>
      )}
    </button>
  );
}

export default OutlineButton;
